import React from 'react';
import { Power } from 'lucide-react';
import { DeviceControl } from '../DeviceControl/DeviceControl';
import { DimmableLightControl } from '../DeviceControl/DimmableLightControl';
import { TemperatureControl } from '../DeviceControl/TemperatureControl';
import { CurtainControl } from '../DeviceControl/CurtainControl';
import { useRoomStore } from '../../../stores/roomStore';
import { AC_MODE, DEVICE_ICON_MAP, DEVICE_KIND, PERCENT, TEMPERATURE_C } from '../../../lib/device/catalog';
import { TRAVEL } from '../../../lib/rcu/catalog/protocol';
import type { Device } from '../../../types/room';

/**
 * The demo surface, shown while no RCU load is wired yet: ten illustrative
 * devices backed by the mock store, so the guest UI is never empty.
 */
export const DemoControls: React.FC = () => {
  const {
    devices,
    toggleDevice,
    setBrightness,
    setTemperature,
    setACMode,
    setCurtainPosition,
  } = useRoomStore();

  const renderDeviceControl = (device: Device) => {
    const Icon = DEVICE_ICON_MAP[device.icon] ?? Power;
    const icon = <Icon className="w-6 h-6" />;

    switch (device.type) {
      case DEVICE_KIND.DIMMABLE_LIGHT:
        return (
          <DimmableLightControl
            key={device.id}
            id={device.id}
            name={device.name}
            icon={icon}
            status={device.status}
            brightness={device.brightness ?? PERCENT.DEFAULT}
            isOnline={device.isOnline}
            onToggle={toggleDevice}
            onBrightnessChange={setBrightness}
          />
        );

      case DEVICE_KIND.AC:
        return (
          <TemperatureControl
            key={device.id}
            id={device.id}
            name={device.name}
            icon={icon}
            status={device.status}
            targetTemp={device.targetTemp ?? TEMPERATURE_C.DEFAULT_TARGET}
            mode={device.mode ?? AC_MODE.COOL}
            isOnline={device.isOnline}
            onToggle={toggleDevice}
            onTemperatureChange={setTemperature}
            onModeChange={setACMode}
          />
        );

      case DEVICE_KIND.CURTAIN:
        return (
          <CurtainControl
            key={device.id}
            id={device.id}
            name={device.name}
            icon={icon}
            status={device.status}
            isOnline={device.isOnline}
            onToggle={toggleDevice}
            onTravel={(_id, travel) => {
              // Demo devices have no motion model, so open and close jump to the
              // ends and stop has nothing to interrupt.
              if (travel === TRAVEL.OPEN) setCurtainPosition(device.id, PERCENT.MAX);
              if (travel === TRAVEL.CLOSE) setCurtainPosition(device.id, PERCENT.MIN);
            }}
          />
        );

      default:
        return (
          <DeviceControl
            key={device.id}
            id={device.id}
            name={device.name}
            icon={icon}
            status={device.status}
            isOnline={device.isOnline}
            onToggle={toggleDevice}
          />
        );
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {devices.map((device) => renderDeviceControl(device))}
    </div>
  );
};

export default DemoControls;
